import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Printer, Ticket, User } from '../models/RestModels';
import { OrderInfo, PaymentInfo } from '../models/models';
import { TicketBuilder } from '../classes/TicketBuilder';
import { CashCloseTicketBuilder, CashCloseTicketData } from '../classes/CashCloseTicketBuilder';
import { WithdrawalTicketBuilder, WithdrawalTicketData } from '../classes/WithdrawalTicketBuilder';
import { ReturnReceiptTicketBuilder, ReturnReceiptTicketData } from '../classes/ReturnReceiptTicketBuilder';
import { StockReportTicketBuilder, StockReportTicketData } from '../classes/StockReportTicketBuilder';
import { ExpenseTicketBuilder } from '../classes/ExpenseTicketBuilder';
import { PaymentReceiptTicketBuilder, PaymentReceiptTicketData } from '../classes/PaymentReceiptTicketBuilder';

// Type for the global print function
type PrintUsbFunction = (xmlString: string, evt: Event | null, config: PrinterUsbConfig, interfaceNumber: number, encoderConfig?: PrinterEncoderConfig) => Promise<string>;

// Declare global functions from printer-library.js
declare const _printUsb: PrintUsbFunction;

export interface PrinterUsbConfig {
	vendorId: number;
	productId: number;
	serial_number?: string;
}

export interface PrinterEncoderConfig {
	language: string;
	columns: number;
	printerModel: string;
	codepageMapping: string;
}

export interface PrintResult {
	success: boolean;
	message: string;
	error?: any;
}

/**
 * Local printer preferences stored in localStorage
 * Device-level configuration for which printers to use
 */
export interface LocalPrinterPreferences {
	// Print method: 'USB' or 'BROWSER'
	print_method: 'USB' | 'BROWSER';

	// Selected printer IDs for this device
	pos_printer_id: number | null;      // For receipts, cash close, reports
	kitchen_printer_ids: number[];       // For commanda (can be multiple)
}

export const PRINTER_PREFERENCES_KEY = 'usb_printer_preferences';

/**
 * Line widths accepted by receipt-printer-encoder.esm.js.
 * The encoder constructor throws "The width of the paper must me either
 * 32, 35, 42, 44 or 48 columns" for any other value, regardless of model.
 */
export const ENCODER_COLUMN_WIDTHS = [32, 35, 42, 44, 48];

/**
 * USB print decision shared by every receipt entry point.
 * Prints via USB whenever the effective preference (local device, seeded
 * from the user's server-side printer when empty) selects USB with a POS
 * printer and the receipt has an id.
 */
export function shouldPrintUsbTicket(prefs: LocalPrinterPreferences, ticket_id: number | null | undefined): boolean {
	return prefs.print_method === 'USB' && !!prefs.pos_printer_id && !!ticket_id;
}

/**
 * Cash-close USB print decision shared by every cash-close entry point.
 * The USB output always follows the cashclose2 receipt format.
 */
export function shouldPrintCashCloseUsb(prefs: LocalPrinterPreferences, cash_close_id: number | null | undefined): boolean {
	return shouldPrintUsbTicket(prefs, cash_close_id);
}

/**
 * Snap a configured column count to the nearest width the encoder accepts.
 * Missing/invalid values fall back to 42 (font A default).
 */
export function snapEncoderColumns(columns: number): number {
	if (!columns || isNaN(columns)) {
		return 42;
	}
	let best = ENCODER_COLUMN_WIDTHS[0];
	for (const width of ENCODER_COLUMN_WIDTHS) {
		if (Math.abs(width - columns) < Math.abs(best - columns)) {
			best = width;
		}
	}
	return best;
}

/**
 * Font-B width used for ticket layout math (tables, dotted rows).
 * Uses the printer's configured font_b_columns when valid, clamped to the
 * same 20-80 range the printer config screen enforces; falls back to the
 * font-A width + 8 when missing or invalid (old rows, unsaved config).
 */
export function resolveFontBColumns(printer: Printer, columns: number): number {
	const configured = Number(printer?.font_b_columns);
	if (configured && !isNaN(configured)) {
		return Math.min(80, Math.max(20, Math.round(configured)));
	}
	return columns + 8;
}

@Injectable({
	providedIn: 'root'
})
export class PrinterService {
	private printResult = new Subject<PrintResult>();
	private currentPrinter: Printer | null = null;
	private currentTicket: Ticket | null = null;

	available_printers: Printer[] = [];
	debug: boolean = false;

	constructor() {
		(window as any).printerDebug = (enabled: boolean) => this.setDebug(enabled);
	}

	private isPrinterLibraryLoaded(): boolean {
		return typeof (window as any)._printUsb === 'function';
	}

	private getPrintUsbFunction(): PrintUsbFunction {
		if (!this.isPrinterLibraryLoaded()) {
			throw new Error('Libreria de impresion no cargada. Recargue la pagina.');
		}
		return (window as any)._printUsb;
	}

	/**
	 * Set the current printer configuration
	 */
	setPrinter(printer: Printer): void {
		this.currentPrinter = printer;
	}

	/**
	 * Get current printer configuration
	 */
	getPrinter(): Printer | null {
		return this.currentPrinter;
	}

	setTicket(ticket: Ticket | null): void {
		this.currentTicket = ticket;
	}

	getTicket(): Ticket | null {
		return this.currentTicket;
	}

	getPosPrinterWithTicket(): { printer: Printer | null; ticket: Ticket | null } {
		return {
			printer: this.getPosPrinter(),
			ticket: this.currentTicket
		};
	}

	/**
	 * Parse hex string to number (e.g., "0x1504" -> 5380)
	 */
	private parseHexId(hexString: string): number {
		if (!hexString) {
			throw new Error('Vendor ID o Product ID no configurado. Edite la impresora para agregar estos valores.');
		}
		if (hexString.startsWith('0x') || hexString.startsWith('0X')) {
			return parseInt(hexString, 16);
		}
		return parseInt(hexString, 10);
	}

	/**
	 * Build USB config from Printer model
	 */
	private buildUsbConfig(printer: Printer): PrinterUsbConfig {
		return {
			vendorId: this.parseHexId(printer.vendor_id),
			productId: this.parseHexId(printer.product_id),
			serial_number: printer.serial_number || undefined
		};
	}

	/**
	 * Build encoder config from Printer model
	 */
	buildEncoderConfig(printer: Printer): PrinterEncoderConfig {
		const columns = snapEncoderColumns(printer.font_a_columns);
		if (columns !== printer.font_a_columns) {
			console.warn(`font_a_columns=${printer.font_a_columns} is not supported by the encoder, using ${columns}. Valid widths: ${ENCODER_COLUMN_WIDTHS.join(', ')}`);
		}
		return {
			language: 'esc-pos',
			columns: columns,
			printerModel: printer.model || 'bixolon-srp350iii',
			codepageMapping: printer.codepage_mapping || 'bixolon'
		};
	}

	// ==========================================
	// Local Preferences Management
	// ==========================================

	/**
	 * Get default preferences
	 */
	private getDefaultPreferences(): LocalPrinterPreferences {
		return {
			print_method: 'BROWSER',
			pos_printer_id: null,
			kitchen_printer_ids: []
		};
	}

	/**
	 * Load preferences from localStorage
	 */
	loadLocalPreferences(): LocalPrinterPreferences {
		try {
			const stored = localStorage.getItem(PRINTER_PREFERENCES_KEY);
			if (stored) {
				const parsed = JSON.parse(stored);
				// Ensure all fields exist
				return {
					print_method: parsed.print_method || 'BROWSER',
					pos_printer_id: parsed.pos_printer_id ?? null,
					kitchen_printer_ids: parsed.kitchen_printer_ids || []
				};
			}
		} catch (e) {
			console.warn('Error loading printer preferences:', e);
		}
		return this.getDefaultPreferences();
	}

	/**
	 * Save preferences to localStorage
	 */
	saveLocalPreferences(prefs: LocalPrinterPreferences): void {
		try {
			localStorage.setItem(PRINTER_PREFERENCES_KEY, JSON.stringify(prefs));
		} catch (e) {
			console.warn('Error saving printer preferences:', e);
		}
	}

	/**
	 * Resolve effective preferences: localStorage first, then the user's
	 * server-side printer_id (user.printer_id) as fallback.
	 *
	 * When localStorage has no POS printer but the user record does, the
	 * server value seeds this device (USB is implied) and is cached locally.
	 * Local selection always wins when present; nothing is overwritten.
	 */
	resolvePreferences(user: User | null): LocalPrinterPreferences {
		const prefs = this.loadLocalPreferences();

		if (prefs.pos_printer_id) {
			return prefs;
		}

		const printer_id = user?.printer_id ?? null;

		if (!printer_id) {
			return prefs;
		}

		const resolved: LocalPrinterPreferences = {
			print_method: 'USB',
			pos_printer_id: printer_id,
			kitchen_printer_ids: prefs.kitchen_printer_ids || []
		};
		this.saveLocalPreferences(resolved);
		return resolved;
	}

	/**
	 * Initialize printer preferences with auto-assignment logic
	 * Called after printers are loaded from the server
	 */
	initializePrinterPreferences(storePrinters: Printer[]): void {
		this.available_printers = storePrinters;
		const local = this.loadLocalPreferences();

		// If localStorage is empty and only 1 printer exists, auto-assign as POS printer
		if (!local.pos_printer_id && storePrinters.length === 1) {
			local.pos_printer_id = storePrinters[0].id;
			local.print_method = 'USB';
			this.saveLocalPreferences(local);
			return;
		}

		// Validate stored IDs still exist in store's printers
		let changed = false;

		if (local.pos_printer_id && !storePrinters.find(p => p.id === local.pos_printer_id)) {
			local.pos_printer_id = null;
			changed = true;
		}

		const validKitchenIds = local.kitchen_printer_ids.filter(
			id => storePrinters.find(p => p.id === id)
		);
		if (validKitchenIds.length !== local.kitchen_printer_ids.length) {
			local.kitchen_printer_ids = validKitchenIds;
			changed = true;
		}

		if (changed) {
			this.saveLocalPreferences(local);
		}
	}

	// ==========================================
	// Printer Selection Methods
	// ==========================================

	/**
	 * Get the POS printer based on local preferences
	 */
	getPosPrinter(): Printer | null {
		const prefs = this.loadLocalPreferences();
		console.log('getPosPrinter - prefs:', prefs);
		console.log('getPosPrinter - available_printers count:', this.available_printers.length);

		if (prefs.print_method !== 'USB' || !prefs.pos_printer_id) {
			console.log('getPosPrinter - USB not enabled or no printer selected');
			return null;
		}
		
		const printer = this.available_printers.find(p => p.id === prefs.pos_printer_id) || null;
		console.log('getPosPrinter - found printer:', printer ? printer.name : 'null');
		return printer;
	}

	/**
	 * Get kitchen printers based on local preferences
	 */
	getKitchenPrinters(): Printer[] {
		const prefs = this.loadLocalPreferences();
		if (prefs.print_method !== 'USB' || prefs.kitchen_printer_ids.length === 0) {
			return [];
		}
		return this.available_printers.filter(p => prefs.kitchen_printer_ids.includes(p.id));
	}

	/**
	 * Check if USB printing is enabled
	 */
	isUsbPrintingEnabled(): boolean {
		const prefs = this.loadLocalPreferences();
		const result = prefs.print_method === 'USB' && prefs.pos_printer_id !== null;
		console.log('PrinterService.isUsbPrintingEnabled - prefs:', prefs, 'available_printers:', this.available_printers.length, 'result:', result);
		return result;
	}

	/**
	 * Check if USB kitchen printing is enabled
	 */
	isUsbKitchenPrintingEnabled(): boolean {
		const prefs = this.loadLocalPreferences();
		return prefs.print_method === 'USB' && prefs.kitchen_printer_ids.length > 0;
	}

	/**
	 * Smart print - uses USB if configured, otherwise returns null for browser fallback
	 * @param xml The ESC/POS XML to print
	 * @param evt The triggering event (required for WebUSB)
	 * @returns PrintResult if USB printing attempted, null if should use browser printing
	 */
	async smartPrint(xml: string, evt: Event | null): Promise<PrintResult | null> {
		console.log('smartPrint called, available_printers:', this.available_printers);
		const printer = this.getPosPrinter();
		console.log('getPosPrinter returned:', printer);
		if (printer) {
			console.log('Printer vendor_id:', printer.vendor_id, 'product_id:', printer.product_id);
		}

		if (!printer) {
			return null; // Signal to use browser printing
		}

		try {
			return await this.print(xml, printer, evt);
		} catch (error) {
			console.warn('USB print failed:', error);
			return null; // Fallback to browser
		}
	}

	/**
	 * Smart print to kitchen printers
	 * @param xml The ESC/POS XML to print
	 * @param evt The triggering event (required for WebUSB)
	 * @returns Array of print results, or null if no kitchen printers configured
	 */
	async smartPrintKitchen(xml: string, evt: Event | null): Promise<PrintResult[] | null> {
		const printers = this.getKitchenPrinters();
		if (printers.length === 0) {
			return null; // Signal to use browser printing
		}

		const results: PrintResult[] = [];
		for (const printer of printers) {
			try {
				const result = await this.print(xml, printer, evt);
				results.push(result);
			} catch (error: any) {
				results.push({
					success: false,
					message: `Error printing to ${printer.name}`,
					error: error
				});
			}
		}
		return results;
	}

	// ==========================================
	// Core Print Methods
	// ==========================================

	/**
	 * Test connection to USB printer
	 * Requires user interaction (click event) due to WebUSB security
	 */
	async testConnection(printer: Printer, evt: Event | null = null): Promise<PrintResult> {
		try {
			const usbConfig = this.buildUsbConfig(printer);
			const encoderConfig = this.buildEncoderConfig(printer);

			console.log('Testing connection with config:', usbConfig, encoderConfig);

			const testXml = `<EscPos>
				<Init/>
				<Text justification="center" bold="true">TEST DE CONEXION</Text>
				<Feed lines="1"/>
				<Text justification="center">Impresora configurada correctamente</Text>
				<Feed lines="1"/>
				<Text justification="center">${printer.name || 'Sin nombre'}</Text>
				<Text justification="center">Modelo: ${printer.model}</Text>
				<Feed lines="3"/>
				<Cut mode="partial"/>
			</EscPos>`;

			const printUsb = this.getPrintUsbFunction();
			await printUsb(testXml, evt, usbConfig, printer.interface_number || 0, encoderConfig);

			const result: PrintResult = {
				success: true,
				message: 'Conexion exitosa'
			};
			this.printResult.next(result);
			return result;
		} catch (error: any) {
			console.error('USB connection error:', error);

			// Provide more detailed error message
			let errorMessage = 'Error de conexion';
			if (error?.message) {
				errorMessage = error.message;
			} else if (error?.name === 'NotFoundError') {
				errorMessage = 'Impresora no encontrada. Verifique que este conectada y los IDs sean correctos.';
			} else if (error?.name === 'SecurityError') {
				errorMessage = 'Permiso denegado. Debe hacer clic en el boton para conectar.';
			} else if (error?.name === 'NetworkError') {
				errorMessage = 'Error de red USB. Intente desconectar y reconectar la impresora.';
			} else if (typeof error === 'string') {
				errorMessage = error;
			}

			const result: PrintResult = {
				success: false,
				message: errorMessage,
				error: error
			};
			this.printResult.next(result);
			return result;
		}
	}

	/**
	 * Print ESC/POS XML to the configured printer
	 * Requires user interaction (click event) due to WebUSB security
	 */
	async print(xml: string, printer?: Printer, evt: Event | null = null): Promise<PrintResult> {
		const targetPrinter = printer || this.currentPrinter;

		if (!targetPrinter) {
			const result: PrintResult = {
				success: false,
				message: 'No hay impresora configurada'
			};
			this.printResult.next(result);
			return result;
		}

		if (this.debug) {
			console.log('=== PRINTER DEBUG XML ===');
			console.log(xml);
			console.log('=== END XML ===');
		}

		try {
			const usbConfig = this.buildUsbConfig(targetPrinter);
			const encoderConfig = this.buildEncoderConfig(targetPrinter);

			console.log('Printing with config:', usbConfig, encoderConfig);

			const printUsb = this.getPrintUsbFunction();
			await printUsb(xml, evt, usbConfig, targetPrinter.interface_number || 0, encoderConfig);

			const result: PrintResult = {
				success: true,
				message: 'Impresion exitosa'
			};
			this.printResult.next(result);
			return result;
		} catch (error: any) {
			console.error('Print error details:', error);

			// Extract meaningful error message
			let errorMessage = 'Error al imprimir';
			if (error?.message) {
				errorMessage = error.message;
			} else if (error?.name === 'NotFoundError') {
				errorMessage = 'Impresora no encontrada. Verifique conexion USB.';
			} else if (error?.name === 'SecurityError') {
				errorMessage = 'Permiso denegado. Requiere clic del usuario.';
			} else if (error?.name === 'NetworkError') {
				errorMessage = 'Error de red USB. Reconecte la impresora.';
			} else if (typeof error === 'string') {
				errorMessage = error;
			}

			const result: PrintResult = {
				success: false,
				message: errorMessage,
				error: error
			};
			this.printResult.next(result);
			return result;
		}
	}

	/**
	 * Observable for print results
	 */
	onPrintResult() {
		return this.printResult.asObservable();
	}

	/**
	 * Enable/disable debug mode to log XML before printing
	 * Usage from console: window.printerDebug(true) or window.printerDebug(false)
	 */
	setDebug(enabled: boolean): void {
		this.debug = enabled;
		console.log('Printer debug mode:', enabled ? 'ENABLED' : 'DISABLED');
	}

	/**
	 * Print order receipt using TicketBuilder
	 * @param orderInfo Complete order information
	 * @param copies Number of copies to print
	 * @param evt Triggering event (required for WebUSB)
	 * @param ticket Optional ticket configuration
	 * @param paymentInfo Optional payment information
	 * @returns PrintResult with success/failure
	 */
	async printReceipt(
		orderInfo: OrderInfo,
		copies: number,
		evt: Event | null,
		ticket?: Ticket,
		paymentInfo?: PaymentInfo
	): Promise<PrintResult> {
		const printer = this.getPosPrinter();

		if (!printer) {
			return {
				success: false,
				message: 'No hay impresora USB configurada. Configure la impresora en ajustes.'
			};
		}

		try {
			// Ticket width must equal the encoder line width or tables overflow the line.
			const columns = this.buildEncoderConfig(printer).columns;
			const calculationStyle = ticket?.calculation_style || printer.calculation_style || 'NORMAL';
			const compactItems = printer.compact_items || false;

			const ticketBuilder = new TicketBuilder(
				orderInfo,
				ticket || null,
				columns,
				resolveFontBColumns(printer, columns),
				calculationStyle,
				compactItems,
				paymentInfo || null
			);

			const xml = ticketBuilder.buildXml();

			for (let i = 0; i < copies; i++) {
				const result = await this.print(xml, printer, evt);
				if (!result.success) {
					return result;
				}
			}

			return { success: true, message: 'Impresion exitosa' };
		} catch (error: any) {
			return {
				success: false,
				message: error?.message || 'Error desconocido',
				error: error
			};
		}
	}

	/**
	 * Print cash close receipt (same content as print-cash-closing2) using CashCloseTicketBuilder
	 * @param data Display-ready cash close data
	 * @param copies Number of copies to print
	 * @param evt Triggering event (required for WebUSB)
	 * @returns PrintResult with success/failure
	 */
	async printCashClose(
		data: CashCloseTicketData,
		copies: number,
		evt: Event | null
	): Promise<PrintResult> {
		const printer = this.getPosPrinter();

		if (!printer) {
			return {
				success: false,
				message: 'No hay impresora USB configurada. Configure la impresora en ajustes.'
			};
		}

		try {
			// Ticket width must equal the encoder line width or tables overflow the line.
			const columns = this.buildEncoderConfig(printer).columns;

			const builder = new CashCloseTicketBuilder(
				data,
				columns,
				resolveFontBColumns(printer, columns)
			);

			const xml = builder.buildXml();

			for (let i = 0; i < copies; i++) {
				const result = await this.print(xml, printer, evt);
				if (!result.success) {
					return result;
				}
			}

			return { success: true, message: 'Impresion exitosa' };
		} catch (error: any) {
			return {
				success: false,
				message: error?.message || 'Error desconocido',
				error: error
			};
		}
	}

	/**
	 * Print withdrawal receipt (same content as print-withdrawal) using WithdrawalTicketBuilder
	 * @param data Display-ready withdrawal receipt data
	 * @param copies Number of copies to print
	 * @param evt Triggering event (required for WebUSB)
	 * @returns PrintResult with success/failure
	 */
	async printWithdrawal(
		data: WithdrawalTicketData,
		copies: number,
		evt: Event | null
	): Promise<PrintResult> {
		const printer = this.getPosPrinter();

		if (!printer) {
			return {
				success: false,
				message: 'No hay impresora USB configurada. Configure la impresora en ajustes.'
			};
		}

		try {
			// Ticket width must equal the encoder line width or tables overflow the line.
			const columns = this.buildEncoderConfig(printer).columns;

			const builder = new WithdrawalTicketBuilder(
				data,
				columns,
				resolveFontBColumns(printer, columns)
			);

			const xml = builder.buildXml();

			for (let i = 0; i < copies; i++) {
				const result = await this.print(xml, printer, evt);
				if (!result.success) {
					return result;
				}
			}

			return { success: true, message: 'Impresion exitosa' };
		} catch (error: any) {
			return {
				success: false,
				message: error?.message || 'Error desconocido',
				error: error
			};
		}
	}

	/**
	 * Print stock report (same content as stock-search-report print) using StockReportTicketBuilder
	 * @param data Display-ready stock report data
	 * @param copies Number of copies to print
	 * @param evt Triggering event (required for WebUSB)
	 * @returns PrintResult with success/failure
	 */
	async printStockReport(
		data: StockReportTicketData,
		copies: number,
		evt: Event | null
	): Promise<PrintResult> {
		const printer = this.getPosPrinter();

		if (!printer) {
			return {
				success: false,
				message: 'No hay impresora USB configurada. Configure la impresora en ajustes.'
			};
		}

		try {
			// Ticket width must equal the encoder line width or tables overflow the line.
			const columns = this.buildEncoderConfig(printer).columns;

			const builder = new StockReportTicketBuilder(
				data,
				columns,
				resolveFontBColumns(printer, columns)
			);

			const xml = builder.buildXml();

			console.log('StockReport XML:', xml);

			for (let i = 0; i < copies; i++) {
				const result = await this.print(xml, printer, evt);
				if (!result.success) {
					return result;
				}
			}

			return { success: true, message: 'Impresion exitosa' };
		} catch (error: any) {
			return {
				success: false,
				message: error?.message || 'Error desconocido',
				error: error
			};
		}
	}

	/**
	 * Print expense receipt (same content as print-payment-expense) using ExpenseTicketBuilder
	 * @param paymentInfo Complete expense payment information
	 * @param copies Number of copies to print
	 * @param evt Triggering event (required for WebUSB)
	 * @returns PrintResult with success/failure
	 */
	async printExpense(
		paymentInfo: PaymentInfo,
		copies: number,
		evt: Event | null
	): Promise<PrintResult> {
		const printer = this.getPosPrinter();

		if (!printer) {
			return {
				success: false,
				message: 'No hay impresora USB configurada. Configure la impresora en ajustes.'
			};
		}

		try {
			// Ticket width must equal the encoder line width or tables overflow the line.
			const columns = this.buildEncoderConfig(printer).columns;

			const builder = new ExpenseTicketBuilder(
				paymentInfo,
				columns,
				resolveFontBColumns(printer, columns)
			);

			const xml = builder.buildXml();

			for (let i = 0; i < copies; i++) {
				const result = await this.print(xml, printer, evt);
				if (!result.success) {
					return result;
				}
			}

			return { success: true, message: 'Impresion exitosa' };
		} catch (error: any) {
			return {
				success: false,
				message: error?.message || 'Error desconocido',
				error: error
			};
		}
	}

	/**
	 * Print payment receipt (installment receipt for one order, or one
	 * payment covering several orders) using PaymentReceiptTicketBuilder
	 * @param data Display-ready payment receipt data
	 * @param copies Number of copies to print
	 * @param evt Triggering event (required for WebUSB)
	 * @param ticket Optional ticket config for header/footer branding
	 * @returns PrintResult with success/failure
	 */
	async printPaymentReceipt(
		data: PaymentReceiptTicketData,
		copies: number,
		evt: Event | null,
		ticket?: Ticket | null
	): Promise<PrintResult> {
		const printer = this.getPosPrinter();

		if (!printer) {
			return {
				success: false,
				message: 'No hay impresora USB configurada. Configure la impresora en ajustes.'
			};
		}

		try {
			// Ticket width must equal the encoder line width or tables overflow the line.
			const columns = this.buildEncoderConfig(printer).columns;

			const builder = new PaymentReceiptTicketBuilder(
				data,
				columns,
				resolveFontBColumns(printer, columns),
				ticket || null
			);

			const xml = builder.buildXml();

			for (let i = 0; i < copies; i++) {
				const result = await this.print(xml, printer, evt);
				if (!result.success) {
					return result;
				}
			}

			return { success: true, message: 'Impresion exitosa' };
		} catch (error: any) {
			return {
				success: false,
				message: error?.message || 'Error desconocido',
				error: error
			};
		}
	}

	/**
	 * Print return receipt (same content as print-return-receipt /
	 * print-cancellation-item-receipt) using ReturnReceiptTicketBuilder
	 * @param data Display-ready return receipt data
	 * @param copies Number of copies to print
	 * @param evt Triggering event (required for WebUSB)
	 * @returns PrintResult with success/failure
	 */
	async printReturnReceipt(
		data: ReturnReceiptTicketData,
		copies: number,
		evt: Event | null
	): Promise<PrintResult> {
		const printer = this.getPosPrinter();

		if (!printer) {
			return {
				success: false,
				message: 'No hay impresora USB configurada. Configure la impresora en ajustes.'
			};
		}

		try {
			// Ticket width must equal the encoder line width or tables overflow the line.
			const columns = this.buildEncoderConfig(printer).columns;

			const builder = new ReturnReceiptTicketBuilder(
				data,
				columns,
				resolveFontBColumns(printer, columns)
			);

			const xml = builder.buildXml();

			for (let i = 0; i < copies; i++) {
				const result = await this.print(xml, printer, evt);
				if (!result.success) {
					return result;
				}
			}

			return { success: true, message: 'Impresion exitosa' };
		} catch (error: any) {
			return {
				success: false,
				message: error?.message || 'Error desconocido',
				error: error
			};
		}
	}
}
